import { SearchInput } from "@/components/flexkit/SearchInput";
import type { TableSortOrder } from "@/components/flexkit/tables/SortableTable";
import { toApiSortOrder } from "@/components/flexkit/tables/sorting-utils";
import { MembersList, type SortColumn } from "@/components/settings/organization/members/MembersList";
import { useMembership } from "@/hooks/use-membership";
import { useMembers } from "@/queries/organization-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { type ListMembersRequest_SortField } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useState, type FC } from "react";
import { useDebounce } from "use-debounce";

export const MembersTab: FC = () => {
    const { data: user } = useAuthenticatedUser();
    const { membership } = useMembership();
    const [searchQuery, setSearchQuery] = useState("");

    const [sortColumn, setSortColumn] = useState<SortColumn | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);

    const [debouncedSearchQuery] = useDebounce(searchQuery, 250, { trailing: true });
    const { data: membersData, isFetching: isMembersLoading } = useMembers({
        filter: {
            search: debouncedSearchQuery,
        },
        sort: {
            field: sortColumn,
            order: toApiSortOrder(sortOrder),
        },
    });

    const handleSort = useCallback((columnName: ListMembersRequest_SortField, order: TableSortOrder) => {
        setSortColumn(columnName);
        setSortOrder(order);
    }, []);

    return (
        <>
            <SearchInput
                wrapperClassName="w-full"
                className="max-w-none"
                placeholder="Search by name, email or ID"
                value={searchQuery || ""}
                onChange={(e) => setSearchQuery(e.target.value.trimStart().replace(/\s+/g, " "))}
                onClear={() => setSearchQuery("")}
                isLoading={isMembersLoading}
                data-tracking-id="search-members-input"
            />
            <MembersList
                user={user}
                membership={membership}
                data={{
                    members: membersData?.members || [],
                }}
                isLoading={isMembersLoading}
                hasSearchQuery={debouncedSearchQuery.trim().length > 0}
                setSort={handleSort}
                sort={{
                    sortBy: sortColumn,
                    sortOrder,
                }}
            />
        </>
    );
};
